import { Flex, Text } from '@chakra-ui/react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  FormFeedback,
  Input,
  OutlineCard,
  TextArea,
  useToast
} from '@morressier/alembic';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ensureUser } from 'src/api/queries';
import { FieldLabel } from 'src/components/Field';
import { Overlay } from 'src/components/Overlay';
import { SubmissionInfoCard } from 'src/components/SubmissionInfo';
import { useReviewData } from 'src/contexts/ReviewDataProvider';
import {
  useInviteCoReviewer,
  useCheckCoReviewerEligibility
} from 'src/api/mutations';
import { z } from 'zod';
import { ConfirmExitModal, ConfirmInvitationModal } from './CoReviewModals';
import { useKeycloak } from 'src/components/AuthHOC';
import ProtectedAction from 'src/components/ProtectedAction';

const coReviewerInviteFormSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required.')
    .email('Please enter a valid Email.'),
  invitationNote: z.string().min(1, 'Please enter a Note For Reviewer.')
});

export type ICoReviewerInviteFormPayload = z.infer<
  typeof coReviewerInviteFormSchema
>;

export const CoReviewFlow = () => {
  const { review, refetchReview, organizationId } = useReviewData();
  const navigate = useNavigate();
  const toast = useToast();
  const { currentUser } = useKeycloak();

  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [showConfirmInvitation, setShowConfirmInvitation] = useState(false);
  const { mutateAsync: inviteCoReviewer, isLoading: isInviting } =
    useInviteCoReviewer(
      review.caseData.submission.id,
      review.submissionContextId,
      organizationId
    );

  const navigateToReview = () => {
    navigate(`/app/reviews/${review.id}`);
  };

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm<ICoReviewerInviteFormPayload>({
    resolver: zodResolver(coReviewerInviteFormSchema),
    defaultValues: {
      email: '',
      invitationNote: ''
    }
  });

  const { mutateAsync: checkEligibility, isLoading: isCheckingEligibility } =
    useCheckCoReviewerEligibility();

  const handleInvite = async () => {
    const data = getValues();
    try {
      const result = await checkEligibility({
        reviewId: review.id,
        email: data.email
      });

      if (result.eligible) {
        clearErrors('email');
        const { userId } = await ensureUser(data.email);

        await inviteCoReviewer(
          {
            reviewRequestId: review.id,
            userId: currentUser.id,
            coReviewerId: userId,
            formPayload: {
              email: data.email,
              invitationNote: data.invitationNote
            }
          },
          {
            onSuccess: () => {
              setShowConfirmInvitation(false);
              toast({
                title: `Co-Reviewer Invite Sent to ${data.email}`,
                variant: 'success',
                isClosable: true
              });
              navigate(`/app/reviews/${review.id}`);
            },
            onError: () => {
              setShowConfirmInvitation(false);
            }
          }
        );

        refetchReview();
      } else {
        setShowConfirmInvitation(false);
        setError('email', {
          type: 'manual',
          message: 'This Reviewer cannot be invited due to conflict issues.'
        });
      }
    } catch (error) {
      setShowConfirmInvitation(false);
      toast({
        variant: 'error',
        title: 'An error occurred',
        isClosable: true
      });
    }
  };

  const onSubmit = () => {
    setShowConfirmInvitation(true);
  };

  const coReview = review.caseData.coReview;

  useEffect(() => {
    // if a coReviewer has already been invited or allowCoReviewInvite is false, we show a toast and navigate back to the review page
    if (!!coReview?.status || !review.allowCoReviewInvite) {
      toast({
        title: 'Co-Review Invitation Not Allowed',
        description:
          'You cannot invite a Co-Reviewer for this Submission at this time.',
        variant: 'error',
        isClosable: true
      });
      navigate(`/app/reviews/${review.id}`, { replace: true });
    }
  }, [coReview, review]);

  return (
    <Overlay>
      {showExitConfirmation ? (
        <ConfirmExitModal
          onClose={() => setShowExitConfirmation(false)}
          onConfirm={() => {
            navigateToReview();
            toast({
              title: 'Co-Review Invitation Cancelled',
              description:
                'You can still invite a Co-Reviewer from the Additional Information Highlights.',
              variant: 'attention',
              isClosable: true
            });
          }}
        />
      ) : null}

      {showConfirmInvitation ? (
        <ConfirmInvitationModal
          onClose={() => setShowConfirmInvitation(false)}
          onConfirm={handleInvite}
          email={watch('email')}
        />
      ) : null}

      <Flex
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        flexDirection="column"
        backgroundColor="surfaceSecondaryFill"
        height="100vh"
      >
        <Flex
          flex={1}
          justifyContent="center"
          width="100%"
          overflowY="auto"
          alignItems="flex-start"
        >
          {/* ----- Form Area ----- */}
          <Flex width="960px" flexDirection="column" gap={8} pt={32} pb={16}>
            <Text textStyle="headingTitle">Invite Co-Reviewer</Text>
            <Text>
              Add the contact of the Reviewer you wish to invite to review this
              Submission with you, and provide any relevant details that can
              assist them in understanding the context of this invitation.
            </Text>

            <OutlineCard borderless padding={8}>
              <Flex flexDirection="column" gap={2}>
                <Text textStyle="headingOverline" textTransform="uppercase">
                  Submission to Review
                </Text>
                <SubmissionInfoCard entryPoint="coReview" />
              </Flex>
            </OutlineCard>

            <OutlineCard borderless padding={8}>
              <Flex flexDirection="column" gap={4}>
                {/* --- Email --- */}
                <Flex flexDirection="column" gap={2}>
                  <FieldLabel
                    isRequired
                    label="Email"
                    subText={
                      errors.email?.message ||
                      "Provide the Reviewer's professional email."
                    }
                  />
                  <Input
                    type="email"
                    isInvalid={!!errors.email}
                    {...register('email')}
                    placeholder="e.g., fabius@molecularconnections.com"
                  />
                  {errors.email && (
                    <FormFeedback variant="error">
                      {errors.email.message}
                    </FormFeedback>
                  )}
                </Flex>

                {/* --- Note --- */}
                <Flex flexDirection="column" gap={2}>
                  <FieldLabel
                    isRequired
                    label="Note for Reviewer"
                    subText="Leave a note for this Reviewer with instructions and any details to help them review this Submission."
                  />
                  <TextArea
                    {...register('invitationNote')}
                    value={watch('invitationNote')}
                    isInvalid={!!errors.invitationNote}
                    placeholder="Add a note for this reviewer"
                    errorMessage={errors.invitationNote?.message}
                  />
                </Flex>
              </Flex>
            </OutlineCard>
          </Flex>
        </Flex>

        {/* ----- Footer ----- */}
        <Flex
          backgroundColor="surfacePrimaryFill"
          justifyContent="space-between"
          paddingY={4}
          width="100%"
          paddingX={8}
        >
          <Button
            variant="caution"
            onClick={() => setShowExitConfirmation(true)}
          >
            Exit
          </Button>

          <ProtectedAction
            relation="canReview"
            entityType="review"
            entityId={review.id}
          >
            <Button
              type="submit"
              isLoading={isCheckingEligibility || isInviting}
              isDisabled={isCheckingEligibility || isInviting}
            >
              Invite Co-Reviewer
            </Button>
          </ProtectedAction>
        </Flex>
      </Flex>
    </Overlay>
  );
};
